import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { moveFocusWithArrows } from '@/shared/lib/roving-focus';
import { avatarInitial } from '@/shared/lib/graphemes';
import { ROUTES } from '@/shared/constants/routes';
import { Icons } from '@/shared/constants/icons';
import { TRANSITION, motionSafe } from '@/shared/constants/motion';
import { useAuth } from '@/shared/hooks/useAuth';

/** Avatar in the topbar: who is signed in, account settings, log out. */
export function UserMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduced = useReducedMotion() ?? false;
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleLogout = useCallback(async (): Promise<void> => {
    setOpen(false);
    await signOut();
    navigate(ROUTES.AUTH);
  }, [signOut, navigate]);

  if (!user) return null;

  const displayName = (user.user_metadata?.['full_name'] as string | undefined) ?? user.email?.split('@')[0] ?? 'User';
  const avatarUrl = user.user_metadata?.['avatar_url'] as string | undefined;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-11 h-11 rounded-full bg-transparent border-none cursor-pointer flex items-center justify-center hover:bg-[var(--color-surface-3)]"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t('topbar.account')}: ${displayName}`}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
        ) : (
          <span className="w-8 h-8 rounded-full bg-[var(--color-surface-3)] text-[var(--ink-2)] text-ui font-bold flex items-center justify-center" aria-hidden="true">
            {avatarInitial(displayName)}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={reduced ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? undefined : { opacity: 0, y: -4 }}
            transition={motionSafe(TRANSITION.panel, reduced)}
            className="absolute end-0 top-full mt-1.5 w-[240px] bg-[var(--color-surface)] border border-[var(--line)] rounded-xl z-[100] overflow-hidden"
            style={{ boxShadow: 'var(--shadow-pop)' }}
            role="menu"
            aria-label={t('topbar.account')}
            onKeyDown={(e) => moveFocusWithArrows(e, '[role="menuitem"]')}
          >
            <div className="px-3 py-3 border-b border-[var(--line)]">
              <div className="text-ui font-semibold text-[var(--ink)] truncate" dir="auto">{displayName}</div>
              {user.email && <div className="text-meta text-[var(--ink-3)] truncate">{user.email}</div>}
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); navigate(ROUTES.SETTINGS); }}
              className="flex items-center gap-2.5 w-full min-h-[44px] px-3 bg-transparent border-none cursor-pointer text-start text-ui font-medium text-[var(--ink-2)] hover:bg-[var(--color-surface-2)] hover:text-[var(--ink)]"
            >
              <Icons.settings size={16} className="shrink-0" aria-hidden="true" />
              {t('topbar.accountSettings')}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => void handleLogout()}
              className="flex items-center gap-2.5 w-full min-h-[44px] px-3 bg-transparent border-none cursor-pointer text-start text-ui font-medium text-[var(--ink-2)] hover:bg-[var(--bad-soft)] hover:text-[var(--bad)]"
            >
              <Icons.logout size={16} className="shrink-0" aria-hidden="true" />
              {t('topbar.logOut')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
